<template>
  <report-page
    :title="name"
    :data-to-send="dataToSend"
    :buttons-disabled="buttonsDisabled"
    :history-filter="historyFilter"
    :selectedWorkspaceUUID="selectedWorkspace"
  >
    <template v-slot:reportForm>
      <report-field :field-name="$tc('Workspace', 1)">
        <treeselect
          v-model="selectedWorkspace"
          :options="workspaceOptions"
          :disabled="
            !hasPermissionToCreateExport ||
              workspaceSelectionDisabled ||
              !userHasManagementPermission('workspace', 'list')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[0].label, 2)">
        <treeselect
          v-model="reportParameters[0].value"
          @input="resetClosureOptions"
          :options="networkStartingPointOptions"
          :load-options="getNetworkStartingPointOptions"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              technicalSitesFieldDisabled ||
              !userHasPermissionToList('technical_local')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[1].label, 2)">
        <treeselect
          v-model="reportParameters[1].value"
          :options="closureOptions"
          :load-options="loadClosureOptions"
          :auto-load-root-options="false"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              mainFieldsDisabled ||
              !userHasPermissionToList('closure')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[2].label, 2)">
        <treeselect
          v-model="reportParameters[2].value"
          :options="mduOptions"
          :load-options="loadMDUOptions"
          :auto-load-root-options="false"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              mainFieldsDisabled ||
              !userHasPermissionToList('technical_local')
          "
          disableFuzzyMatching
        />
      </report-field>
    </template>
  </report-page>
</template>

<script>
import ReportPage from "../ReportPage";
import ReportField from "../ReportField";
import { homeReportPageMixin } from "@/mixins/homeReportPageMixin";
import _ from "lodash";

export default {
  name: "SplicesReport",

  components: {
    ReportField,
    ReportPage
  },

  mixins: [homeReportPageMixin],

  props: {
    name: {
      type: String,
      required: true
    },
    version: {
      type: String
    }
  },

  data() {
    return {
      reportParameters: [
        {
          label: "TechnicalSite",
          key: "technical_locals",
          type: "array",
          value: []
        },
        {
          label: "Closure",
          key: "closures",
          type: "array",
          value: []
        },
        {
          label: "MDU",
          key: "technical_locals",
          type: "array",
          value: []
        }
      ],

      closureOptions: null,
      mduOptions: null,

      historyFilter: "excel"
    };
  },

  computed: {
    technicalSitesFieldDisabled() {
      return this.selectedWorkspace === undefined;
    },
    mainFieldsDisabled() {
      return this.reportParameters[0].value.length === 0;
    },
    buttonsDisabled() {
      return !(
        this.reportParameters[1].value.length > 0 ||
        this.reportParameters[2].value.length > 0
      );
    },
    dataToSend() {
      let dataToSend = {
        type: this.historyFilter,
        version: this.version,
        objects: {
          workspace_uuid: this.selectedWorkspace
        }
      };
      let closuresToSend = this.fillArrayOfObjectsToSend(
        this.reportParameters[1].value,
        "closure"
      );
      let mdusToSend = this.fillArrayOfObjectsToSend(
        this.reportParameters[2].value,
        "technical_local"
      );
      dataToSend.objects.object = closuresToSend.concat(mdusToSend);
      return dataToSend;
    }
  },

  watch: {
    selectedWorkspace() {
      this.resetReportParameterValues(this.reportParameters, 0);
      this.networkStartingPointOptions = null;
    }
  },

  mounted() {
    this.setWorkspaceField();
  },

  methods: {
    resetClosureOptions() {
      this.closureOptions = null;
      this.mduOptions = null;
    },

    async loadClosureOptions() {
      let selectedTechnicalLocals = this.reportParameters[0].value;
      if (selectedTechnicalLocals.length > 0) {
        await this.getTechnicalLocalsChildrenClosures(selectedTechnicalLocals);
      } else {
        throw new Error(
          "Please select a technical site to get a list of closures."
        );
      }
    },

    getTechnicalLocalsChildrenClosures(technicalLocals) {
      return new Promise((resolve, reject) => {
        let promises = [];
        for (const technicalLocalUUID of technicalLocals) {
          promises.push(
            this.getTechnicalLocalChildren(
              technicalLocalUUID,
              "logicalrelationsclosures"
            )
          );
        }
        Promise.all(promises)
          .then(results => {
            this.processClosureOptionsList(results);
            resolve();
          })
          .catch(() => {
            console.error("At lest one request failed");
            reject();
          });
      });
    },

    processClosureOptionsList(requestResult) {
      let closures = [];
      requestResult.forEach(technicalLocalClosures => {
        closures = Object.assign(closures, technicalLocalClosures);
      });
      closures = _.uniqBy(closures, "cl_id");
      closures = _(closures).sortBy("cl_id");
      let closureOptions = this.optionListNormalizer(closures, "cl");
      this.closureOptions = this.includeInAllOption(closureOptions);
    },

    async loadMDUOptions() {
      let selectedTechnicalLocals = this.reportParameters[0].value;
      if (selectedTechnicalLocals.length > 0) {
        await this.getTechnicalLocalsChildrenTechnicalLocals(
          selectedTechnicalLocals
        );
      } else {
        throw new Error(
          "Please select a technical site to get a list of MDUs."
        );
      }
    },

    getTechnicalLocalsChildrenTechnicalLocals(technicalLocals) {
      return new Promise((resolve, reject) => {
        let promises = [];
        for (const technicalLocalUUID of technicalLocals) {
          promises.push(
            this.getTechnicalLocalChildren(
              technicalLocalUUID,
              "logicalrelationstechnicallocals"
            )
          );
        }
        Promise.all(promises)
          .then(results => {
            this.processMDUOptionsList(results);
            resolve();
          })
          .catch(() => {
            console.error("At lest one request failed");
            reject();
          });
      });
    },

    processMDUOptionsList(requestResult) {
      let technicalLocals = [];
      requestResult.forEach(technicalLocalChildren => {
        technicalLocals = Object.assign(
          technicalLocals,
          technicalLocalChildren
        );
      });
      technicalLocals = _.uniqBy(technicalLocals, "tl_id");
      technicalLocals = _.filter(technicalLocals, tl => {
        return tl.tl_network_level === "DROP";
      });
      technicalLocals = _(technicalLocals).sortBy("tl_id");
      let mduOptions = this.optionListNormalizer(technicalLocals, "tl");
      this.mduOptions = this.includeInAllOption(mduOptions);
    },

    getTechnicalLocalChildren(technicalLocalUUID, logicalRelations) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("GET_TECHNICAL_LOCAL_WITH_LOGICAL_RELATIONS", {
            technicalLocalUUID: technicalLocalUUID,
            workspaceUUID: this.selectedWorkspace,
            includes: [logicalRelations]
          })
          .then(technicalLocal => {
            resolve(technicalLocal[logicalRelations]);
          })
          .catch(error => {
            console.log(error);
            reject();
          });
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Reports/ReportsPages/Splices.vue